import { validateCpf, normalizeCpf } from "src-next/shared/utils/cpf";
import {
  isBlankBirthDateValue,
  REQUIRE_BATCH_PERSON_BIRTH_DATE,
} from "./batchPersonRegisterFlags";
import type { BatchPersonSexInput, BatchPersonValidRow } from "./types";

function stripAccents(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/**
 * Aceita no arquivo: m, f, masculino ou feminino (sem diferenciar maiúsculas).
 * male/female só entram na hora de gravar o Person.
 */
function parseSexInput(raw: string): BatchPersonSexInput | null {
  const key = stripAccents(raw).trim().toLowerCase();
  if (key === "m" || key === "f" || key === "masculino" || key === "feminino") {
    return key;
  }
  return null;
}

/**
 * Aceita somente DD/MM/YYYY. Grava YYYY-MM-DD no Person.
 */
function parseBirthDate(raw: string): string | null {
  const match = raw.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  const isRealDate =
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day;
  if (!isRealDate) return null;
  if (year < 1900 || date.getTime() > Date.now()) return null;

  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * Telefone BR: 10 ou 11 dígitos após remover máscara e +55.
 * Persiste só os dígitos nacionais (padrão Patient da clínica).
 */
function parsePhone(raw: string): string | null {
  let digits = raw.replace(/\D/g, "");
  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) {
    digits = digits.slice(2);
  }
  if (digits.length !== 10 && digits.length !== 11) return null;
  return digits;
}

export function validateBatchPersonRow(input: {
  lineNumber: number;
  cpf: string;
  name: string;
  gender: string;
  birthDate: string;
  phone: string;
}): { ok: true; row: BatchPersonValidRow } | { ok: false; reason: string } {
  const name = input.name.trim();
  if (!name) {
    return { ok: false, reason: "Nome em branco" };
  }

  if (!validateCpf(input.cpf).isValid) {
    return { ok: false, reason: "CPF inválido" };
  }

  const gender = parseSexInput(input.gender);
  if (!gender) {
    return { ok: false, reason: "Sexo deve ser M, F, masculino ou feminino" };
  }

  const birthDate = isBlankBirthDateValue(input.birthDate)
    ? undefined
    : parseBirthDate(input.birthDate) ?? undefined;
  if (REQUIRE_BATCH_PERSON_BIRTH_DATE && !birthDate) {
    return { ok: false, reason: "Data deve estar no formato DD/MM/YYYY" };
  }

  const phone = parsePhone(input.phone);
  if (!phone) {
    return { ok: false, reason: "Telefone inválido" };
  }

  return {
    ok: true,
    row: {
      lineNumber: input.lineNumber,
      cpf: normalizeCpf(input.cpf),
      name,
      gender,
      ...(birthDate ? { birthDate } : {}),
      phone,
    },
  };
}
